/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import * as chars from '../chars';
import {NumberWrapper, isPresent} from '../facade/lang';
import {CompilerInjectable} from '../injectable';
export type TokenType = number;
export let TokenType: any = {};
TokenType.Character = 0;
TokenType.Identifier = 1;
TokenType.Keyword = 2;
TokenType.String = 3;
TokenType.Operator = 4;
TokenType.Number = 5;
TokenType.Error = 6;
TokenType[TokenType.Character] = "Character";
TokenType[TokenType.Identifier] = "Identifier";
TokenType[TokenType.Keyword] = "Keyword";
TokenType[TokenType.String] = "String";
TokenType[TokenType.Operator] = "Operator";
TokenType[TokenType.Number] = "Number";
TokenType[TokenType.Error] = "Error";


const /** @type {?} */ KEYWORDS = ['var', 'let', 'null', 'undefined', 'true', 'false', 'if', 'else', 'this'];

@CompilerInjectable()
export class Lexer {
/**
 * @param {?} text
 * @return {?}
 */
tokenize(text: string): Token[] {
    const /** @type {?} */ scanner = new _Scanner(text);
    const /** @type {?} */ tokens: Token[] = [];
    let /** @type {?} */ token = scanner.scanToken();
    while (token != null) {
      tokens.push(token);
      token = scanner.scanToken();
    }
    return tokens;
  }
}

export class Token {
/**
 * @param {?} index
 * @param {?} type
 * @param {?} numValue
 * @param {?} strValue
 */
constructor(
public index: number,
public type: TokenType,
public numValue: number,
public strValue: string) {}
/**
 * @param {?} code
 * @return {?}
 */
isCharacter(code: number): boolean {
    return this.type == TokenType.Character && this.numValue == code;
  }
/**
 * @return {?}
 */
isNumber(): boolean { return this.type == TokenType.Number; }
/**
 * @return {?}
 */
isString(): boolean { return this.type == TokenType.String; }
/**
 * @param {?} operater
 * @return {?}
 */
isOperator(operater: string): boolean {
    return this.type == TokenType.Operator && this.strValue == operater;
  }
/**
 * @return {?}
 */
isIdentifier(): boolean { return this.type == TokenType.Identifier; }
/**
 * @return {?}
 */
isKeyword(): boolean { return this.type == TokenType.Keyword; }
/**
 * @return {?}
 */
isKeywordLet(): boolean { return this.type == TokenType.Keyword && this.strValue == 'let'; }
/**
 * @return {?}
 */
isKeywordNull(): boolean { return this.type == TokenType.Keyword && this.strValue == 'null'; }
/**
 * @return {?}
 */
isKeywordUndefined(): boolean {
    return this.type == TokenType.Keyword && this.strValue == 'undefined';
  }
/**
 * @return {?}
 */
isKeywordTrue(): boolean { return this.type == TokenType.Keyword && this.strValue == 'true'; }
/**
 * @return {?}
 */
isKeywordFalse(): boolean { return this.type == TokenType.Keyword && this.strValue == 'false'; }
/**
 * @return {?}
 */
isKeywordThis(): boolean { return this.type == TokenType.Keyword && this.strValue == 'this'; }
/**
 * @return {?}
 */
isError(): boolean { return this.type == TokenType.Error; }
/**
 * @return {?}
 */
toNumber(): number { return this.type == TokenType.Number ? this.numValue : -1; }
/**
 * @return {?}
 */
toString(): string {
    switch (this.type) {
      case TokenType.Character:
      case TokenType.Identifier:
      case TokenType.Keyword:
      case TokenType.Operator:
      case TokenType.String:
      case TokenType.Error:
        return this.strValue;
      case TokenType.Number:
        return this.numValue.toString();
      default:
        return null;
    }
  }
}

function Token_tsickle_Closure_declarations() {
 /** @type {?} */
Token.prototype.index;
 /** @type {?} */
Token.prototype.type;
 /** @type {?} */
Token.prototype.numValue;
 /** @type {?} */
Token.prototype.strValue;
}

/**
 * @param {?} index
 * @param {?} code
 * @return {?}
 */
function newCharacterToken(index: number, code: number): Token {
  return new Token(index, TokenType.Character, code, String.fromCharCode(code));
}
/**
 * @param {?} index
 * @param {?} text
 * @return {?}
 */
function newIdentifierToken(index: number, text: string): Token {
  return new Token(index, TokenType.Identifier, 0, text);
}
/**
 * @param {?} index
 * @param {?} text
 * @return {?}
 */
function newKeywordToken(index: number, text: string): Token {
  return new Token(index, TokenType.Keyword, 0, text);
}
/**
 * @param {?} index
 * @param {?} text
 * @return {?}
 */
function newOperatorToken(index: number, text: string): Token {
  return new Token(index, TokenType.Operator, 0, text);
}
/**
 * @param {?} index
 * @param {?} text
 * @return {?}
 */
function newStringToken(index: number, text: string): Token {
  return new Token(index, TokenType.String, 0, text);
}
/**
 * @param {?} index
 * @param {?} n
 * @return {?}
 */
function newNumberToken(index: number, n: number): Token {
  return new Token(index, TokenType.Number, n, '');
}
/**
 * @param {?} index
 * @param {?} message
 * @return {?}
 */
function newErrorToken(index: number, message: string): Token {
  return new Token(index, TokenType.Error, 0, message);
}

export var /** @type {?} */ EOF: Token = new Token(-1, TokenType.Character, 0, '');

class _Scanner {
  length: number;
  peek: number = 0;
  index: number = -1;
/**
 * @param {?} input
 */
constructor(public input: string) {
    this.length = input.length;
    this.advance();
  }
/**
 * @return {?}
 */
advance() {
    this.peek = ++this.index >= this.length ? chars.$EOF : this.input.charCodeAt(this.index);
  }
/**
 * @return {?}
 */
scanToken(): Token {
    const /** @type {?} */ input = this.input, /** @type {?} */ length = this.length;
    let /** @type {?} */ peek = this.peek, /** @type {?} */ index = this.index;

    // Skip whitespace.
    while (peek <= chars.$SPACE) {
      if (++index >= length) {
        peek = chars.$EOF;
        break;
      } else {
        peek = input.charCodeAt(index);
      }
    }

    this.peek = peek;
    this.index = index;

    if (index >= length) {
      return null;
    }

    // Handle identifiers and numbers.
    if (isIdentifierStart(peek)) return this.scanIdentifier();
    if (chars.isDigit(peek)) return this.scanNumber(index);

    const /** @type {?} */ start: number = index;
    switch (peek) {
      case chars.$PERIOD:
        this.advance();
        return chars.isDigit(this.peek) ? this.scanNumber(start) :
                                          newCharacterToken(start, chars.$PERIOD);
      case chars.$LPAREN:
      case chars.$RPAREN:
      case chars.$LBRACE:
      case chars.$RBRACE:
      case chars.$LBRACKET:
      case chars.$RBRACKET:
      case chars.$COMMA:
      case chars.$COLON:
      case chars.$SEMICOLON:
        return this.scanCharacter(start, peek);
      case chars.$SQ:
      case chars.$DQ:
        return this.scanString();
      case chars.$HASH:
      case chars.$PLUS:
      case chars.$MINUS:
      case chars.$STAR:
      case chars.$SLASH:
      case chars.$PERCENT:
      case chars.$CARET:
        return this.scanOperator(start, String.fromCharCode(peek));
      case chars.$QUESTION:
        return this.scanComplexOperator(start, '?', chars.$PERIOD, '.');
      case chars.$LT:
      case chars.$GT:
        return this.scanComplexOperator(start, String.fromCharCode(peek), chars.$EQ, '=');
      case chars.$BANG:
      case chars.$EQ:
        return this.scanComplexOperator(
            start, String.fromCharCode(peek), chars.$EQ, '=', chars.$EQ, '=');
      case chars.$AMPERSAND:
        return this.scanComplexOperator(start, '&', chars.$AMPERSAND, '&');
      case chars.$BAR:
        return this.scanComplexOperator(start, '|', chars.$BAR, '|');
      case chars.$NBSP:
        while (chars.isWhitespace(this.peek)) this.advance();
        return this.scanToken();
    }

    this.advance();
    return this.error(`Unexpected character [${String.fromCharCode(peek)}]`, 0);
  }
/**
 * @param {?} start
 * @param {?} code
 * @return {?}
 */
scanCharacter(start: number, code: number): Token {
    this.advance();
    return newCharacterToken(start, code);
  }
/**
 * @param {?} start
 * @param {?} str
 * @return {?}
 */
scanOperator(start: number, str: string): Token {
    this.advance();
    return newOperatorToken(start, str);
  }
/**
 *  Tokenize a 2/3 char long operator
  * *
 * @param {?} start start index in the expression
 * @param {?} one first symbol (always part of the operator)
 * @param {?} twoCode code point for the second symbol
 * @param {?} two second symbol (part of the operator when the second code point matches)
 * @param {?=} threeCode code point for the third symbol
 * @param {?=} three third symbol (part of the operator when provided and matches source expression)
 * @return {?}
 */
scanComplexOperator(
      start: number, one: string, twoCode: number, two: string, threeCode?: number,
      three?: string): Token {
    this.advance();
    let /** @type {?} */ str: string = one;
    if (this.peek == twoCode) {
      this.advance();
      str += two;
    }
    if (isPresent(threeCode) && this.peek == threeCode) {
      this.advance();
      str += three;
    }
    return newOperatorToken(start, str);
  }
/**
 * @return {?}
 */
scanIdentifier(): Token {
    const /** @type {?} */ start: number = this.index;
    this.advance();
    while (isIdentifierPart(this.peek)) this.advance();
    const /** @type {?} */ str: string = this.input.substring(start, this.index);
    return KEYWORDS.indexOf(str) > -1 ? newKeywordToken(start, str) :
                                        newIdentifierToken(start, str);
  }
/**
 * @param {?} start
 * @return {?}
 */
scanNumber(start: number): Token {
    let /** @type {?} */ simple: boolean = (this.index === start);
    this.advance();  // Skip initial digit.
    while (true) {
      if (chars.isDigit(this.peek)) {
        // Do nothing.
      } else if (this.peek == chars.$PERIOD) {
        simple = false;
      } else if (isExponentStart(this.peek)) {
        this.advance();
        if (isExponentSign(this.peek)) this.advance();
        if (!chars.isDigit(this.peek)) return this.error('Invalid exponent', -1);
        simple = false;
      } else {
        break;
      }
      this.advance();
    }
    const /** @type {?} */ str: string = this.input.substring(start, this.index);
    const /** @type {?} */ value: number = simple ? NumberWrapper.parseIntAutoRadix(str) : parseFloat(str);
    return newNumberToken(start, value);
  }
/**
 * @return {?}
 */
scanString(): Token {
    const /** @type {?} */ start: number = this.index;
    const /** @type {?} */ quote: number = this.peek;
    this.advance();  // Skip initial quote.

    let /** @type {?} */ buffer: string = '';
    let /** @type {?} */ marker: number = this.index;
    const /** @type {?} */ input: string = this.input;

    while (this.peek != quote) {
      if (this.peek == chars.$BACKSLASH) {
        buffer += input.substring(marker, this.index);
        this.advance();
        let /** @type {?} */ unescapedCode: number;
        if (this.peek == chars.$u) {
          // 4 character hex code for unicode character.
          const /** @type {?} */ hex: string = input.substring(this.index + 1, this.index + 5);
          if (/^[0-9a-f]+$/i.test(hex)) {
            unescapedCode = parseInt(hex, 16);
          } else {
            return this.error(`Invalid unicode escape [\\u${hex}]`, 0);
          }
          for (let /** @type {?} */ i: number = 0; i < 5; i++) {
            this.advance();
          }
        } else {
          unescapedCode = unescape(this.peek);
          this.advance();
        }
        buffer += String.fromCharCode(unescapedCode);
        marker = this.index;
      } else if (this.peek == chars.$EOF) {
        return this.error('Unterminated quote', 0);
      } else {
        this.advance();
      }
    }

    const /** @type {?} */ last: string = input.substring(marker, this.index);
    this.advance();  // Skip terminating quote.

    return newStringToken(start, buffer + last);
  }
/**
 * @param {?} message
 * @param {?} offset
 * @return {?}
 */
error(message: string, offset: number): Token {
    const /** @type {?} */ position: number = this.index + offset;
    return newErrorToken(
        position, `Lexer Error: ${message} at column ${position} in expression [${this.input}]`);
  }
}

function _Scanner_tsickle_Closure_declarations() {
 /** @type {?} */
_Scanner.prototype.length;
 /** @type {?} */
_Scanner.prototype.peek;
 /** @type {?} */
_Scanner.prototype.index;
 /** @type {?} */
_Scanner.prototype.input;
}

/**
 * @param {?} code
 * @return {?}
 */
function isIdentifierStart(code: number): boolean {
  return (chars.$a <= code && code <= chars.$z) || (chars.$A <= code && code <= chars.$Z) ||
      (code == chars.$_) || (code == chars.$$);
}
/**
 * @param {?} input
 * @return {?}
 */
export function isIdentifier(input: string): boolean {
  if (input.length == 0) return false;
  const /** @type {?} */ scanner = new _Scanner(input);
  if (!isIdentifierStart(scanner.peek)) return false;
  scanner.advance();
  while (scanner.peek !== chars.$EOF) {
    if (!isIdentifierPart(scanner.peek)) return false;
    scanner.advance();
  }
  return true;
}
/**
 * @param {?} code
 * @return {?}
 */
function isIdentifierPart(code: number): boolean {
  return chars.isAsciiLetter(code) || chars.isDigit(code) || (code == chars.$_) ||
      (code == chars.$$);
}
/**
 * @param {?} code
 * @return {?}
 */
function isExponentStart(code: number): boolean {
  return code == chars.$e || code == chars.$E;
}
/**
 * @param {?} code
 * @return {?}
 */
function isExponentSign(code: number): boolean {
  return code == chars.$MINUS || code == chars.$PLUS;
}
/**
 * @param {?} code
 * @return {?}
 */
export function isQuote(code: number): boolean {
  return code === chars.$SQ || code === chars.$DQ || code === chars.$BT;
}
/**
 * @param {?} code
 * @return {?}
 */
function unescape(code: number): number {
  switch (code) {
    case chars.$n:
      return chars.$LF;
    case chars.$f:
      return chars.$FF;
    case chars.$r:
      return chars.$CR;
    case chars.$t:
      return chars.$TAB;
    case chars.$v:
      return chars.$VTAB;
    default:
      return code;
  }
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
